import React from "react";

const MATCH_CONTEXT_LENGTH = 20;

/**
 * マッチ部分をハイライト表示するためのテキストを生成（コンテキスト付き）
 * @param {string} text - 元のテキスト
 * @param {Array} indices - マッチした位置の配列
 * @param {string} highlightClass - ハイライト用のCSSクラス
 * @returns {Array|null} - React要素の配列
 */
export function renderHighlightedText(text, indices, highlightClass) {
  if (!text || !indices || indices.length === 0) return null;

  const elements = [];

  const [start, end] = indices.reduce((longest, current) => {
    const [longestStart, longestEnd] = longest;
    const [currentStart, currentEnd] = current;
    return currentEnd - currentStart > longestEnd - longestStart
      ? current
      : longest;
  }, indices[0]);

  // 前後のコンテキストを計算
  const contextStart = Math.max(0, start - MATCH_CONTEXT_LENGTH);
  const contextEnd = Math.min(text.length, end + 1 + MATCH_CONTEXT_LENGTH);

  // 省略記号
  if (contextStart > 0) {
    elements.push("...");
  }

  // マッチ前のテキスト
  if (start > contextStart) {
    elements.push(text.slice(contextStart, start));
  }

  // マッチ部分をハイライト表示
  elements.push(
    <span key={`match-${start}`} className={highlightClass}>
      {text.slice(start, end + 1)}
    </span>,
  );

  // マッチ後のテキスト
  if (end + 1 < contextEnd) {
    elements.push(text.slice(end + 1, contextEnd));
  }

  // 省略記号
  if (contextEnd < text.length) {
    elements.push("...");
  }

  return elements;
}

/**
 * フィールド全体をハイライト表示（コンテキストなし）
 * @param {string} text - 元のテキスト
 * @param {Object} matchInfo - Fuse.jsのマッチ情報
 * @param {string} className - ハイライト用のCSSクラス
 * @returns {Array|string} - React要素の配列またはテキスト
 */
export function renderFullHighlight(text, matchInfo, className) {
  if (!text || !matchInfo || !matchInfo.indices?.length) return text;
  const elements = [];
  let lastEnd = 0;
  for (const [start, end] of matchInfo.indices) {
    if (start > lastEnd) {
      elements.push(text.slice(lastEnd, start));
    }
    elements.push(
      <span key={start} className={className}>
        {text.slice(start, end + 1)}
      </span>,
    );
    lastEnd = end + 1;
  }
  if (lastEnd < text.length) {
    elements.push(text.slice(lastEnd));
  }
  return elements;
}
